import {
    CreditCardIcon,
    CurrencyDollarIcon,
    HouseIcon,
    InvoiceIcon,
    PenNibIcon,
    SolarPanelIcon,
    SparkleIcon,
    SquaresFourIcon,
    UserPlusIcon,
} from '@phosphor-icons/react';
import { isHoverJobAvailable } from '../../../../services/oneButtonServices/hoverServices';

/** Left-rail dashboard sections: one panel at a time, or `all` for the full grid. */
export type DashboardSectionPanelId =
    | 'all'
    | 'customer'
    | 'utility'
    | 'designs'
    | 'financing'
    | 'system'
    | 'refer'
    | 'quotes'
    | 'agreements';

export type DashboardSectionNavItem = {
    id: DashboardSectionPanelId;
    /** Shown in tooltips and for accessibility */
    label: string;
    /** Uppercase label under icon on expanded section rail */
    railLabel: string;
    Icon: typeof HouseIcon;
    /** Omit from the floating section rail (e.g. full dashboard grid) */
    hideFromRail?: boolean;
    /** Highlighted “card” treatment for the first item (full dashboard) */
    railVariant?: 'card';
    railSepAfter?: boolean;
    railSepBefore?: boolean;
};

/**
 * Rail order: Full dashboard → AI Designs → Opportunity → Utility → System →
 * Quotes → Agreements → Stripe → Refer a friend.
 */
export const DASHBOARD_SECTION_NAV_ITEMS: ReadonlyArray<DashboardSectionNavItem> = [
    {
        id: 'all',
        label: 'Full dashboard',
        railLabel: 'ALL',
        Icon: SquaresFourIcon,
        railVariant: 'card',
    },
    {
        id: 'designs',
        label: 'AI Designs',
        railLabel: 'AI DESIGN',
        Icon: SparkleIcon,
    },
    {
        id: 'customer',
        label: 'Opportunity & contacts',
        railLabel: 'OPPORTUNITY',
        Icon: HouseIcon,
    },
    {
        id: 'utility',
        label: 'Utility details',
        railLabel: 'UTILITY',
        Icon: InvoiceIcon,
    },
    {
        id: 'system',
        label: 'System & design',
        railLabel: 'SYSTEM',
        Icon: SolarPanelIcon,
    },
    {
        id: 'quotes',
        label: 'Quotes',
        railLabel: 'QUOTES',
        Icon: CurrencyDollarIcon,
    },
    {
        id: 'agreements',
        label: 'Agreements',
        railLabel: 'AGREEMENTS',
        Icon: PenNibIcon,
    },
    {
        id: 'financing',
        label: 'Financing options',
        railLabel: 'STRIPE',
        Icon: CreditCardIcon,
    },
    {
        id: 'refer',
        label: 'Refer a friend',
        railLabel: 'REFER A FRIEND',
        Icon: UserPlusIcon,
        // Keep unfinished referral copy out of the production navigation.
        hideFromRail: true,
    },
];

function interestFlag(interest: Record<string, unknown> | null | undefined, key: string): boolean {
    return Boolean(interest?.[key]);
}

function opportunityInterestFromDetails(opportunityDetails: unknown): Record<string, unknown> {
    if (!opportunityDetails || typeof opportunityDetails !== 'object') return {};
    const row = opportunityDetails as Record<string, unknown>;
    const interest = row.OpportunityInterest ?? row.opportunityInterest;
    return interest && typeof interest === 'object' && !Array.isArray(interest)
        ? (interest as Record<string, unknown>)
        : {};
}

/**
 * Angular `SideMenuComponent.updateLinkStatus` for Utility Information and System Design.
 * Utility: Solar, Battery, or BatteryOnly interest, and not RR.
 * System Design: not RR; also hidden when a Hover job is enabled and the deal has no Solar / Battery / BatteryOnly / RRR.
 */
export function getDashboardSideMenuSectionVisibility(options: {
    opportunityDetails: unknown;
    hoverJobId?: string | null;
    enableHoverJob?: boolean;
}): { showUtilityDetails: boolean; showSystemDesign: boolean } {
    const interest = opportunityInterestFromDetails(options.opportunityDetails);
    const isRRProduct = interestFlag(interest, 'RR');
    const showUtilityInfo =
        (interestFlag(interest, 'Solar') ||
            interestFlag(interest, 'BatteryOnly') ||
            interestFlag(interest, 'Battery')) &&
        !isRRProduct;
    const hasNonRoofProduct =
        interestFlag(interest, 'Solar') ||
        interestFlag(interest, 'Battery') ||
        interestFlag(interest, 'BatteryOnly') ||
        interestFlag(interest, 'RRR');
    const hoverHidesSystemDesign =
        isHoverJobAvailable(options.hoverJobId) && Boolean(options.enableHoverJob) && !hasNonRoofProduct;

    return {
        showUtilityDetails: showUtilityInfo,
        showSystemDesign: !isRRProduct && !hoverHidesSystemDesign,
    };
}
